import { useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMediaQuery } from "react-responsive";
import styled from "styled-components";

import {
  useMutationCreateAnnotation,
  useMutationDeleteAnnotation,
  useMutationDeleteResource,
  useMutationUpdateAnnotation,
  useQueryListAnnotations,
  useAppContext,
} from "@examspark/common-app";
import { Button, KeyboardAccessible, Toolbar } from "@examspark/common-ui";
import {
  IconRejectLine,
  IconStickyNoteLine,
} from "@examspark/common-ui/dist/components/icons";
import { IDSK, Annotation, Resource } from "@examspark/types";

import { AsideLayoutContext } from "../../contexts/AsideLayoutContext";
import AnnotationsPanel from "../annotations/AnnotationsPanel";

const StyledAnnotationsArea = styled.div<{ breakAt: string }>`
  @media (min-width: ${({ breakAt }) => breakAt}) {
    position: absolute;
    top: 0;
    left: 5rem;
    width: 45rem;
  }
  border: solid 1px #bbb;
  background-color: #fff;
  border-radius: 0.5rem;
  overflow: hidden;
  z-index: 999;
  box-shadow: 0 0 1rem 0.2rem rgba(112, 91, 91, 0.25);
`;
const AnnotationsTitlebar = styled.div`
  background-color: #fbfbc0;
  padding: 0.8rem 1rem;

  span {
    font-weight: light;
    color: #555;
    font-size: 1.4rem;
  }
`;
const AnnotationsBody = styled.div`
  border-top: solid 1px #e1e1af;
`;

const StyledAnnotationButton = styled(Button)`
  background-color: #fbfbc0;
  border-color: #e2e2c5;

  &:hover {
    background-color: #f5f5c9;
    border-color: #d1d1a7;
  }
`;

type AnnotationsProps = {
  locationKey?: string;
  resource: IDSK;
};

export type CreateAnnotationParams = {
  skResourceId: string;
  bankId?: string;
  /** If `resource` is passed, this is a weblink, file, or some other exotic
   * annotation  */
  resource?: Resource;
};

export const Annotations = ({
  resource: currentUnit,
  locationKey,
}: AnnotationsProps) => {
  const [isAreaOpen, setIsAreaOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    annotationsResult,
    onCreateAnnotation,
    onUpdateAnnotation,
    onDeleteAnnotation,
  } = useAnnotations({ locationKey, resource: currentUnit, setIsLoading });

  const annotations = annotationsResult.data;
  const annotationCount = annotations?.length ?? 0;

  const asideRequest = `annotations-${locationKey}`;
  const {
    onRequestAside,
    asidePortalId,
    breakAt = "1024px",
  } = useContext(AsideLayoutContext);

  const portalTarget = document.getElementById(asidePortalId);

  const shouldBreakIntoAside = useMediaQuery({
    query: `(max-width: ${breakAt})`,
  });

  const AnnotationsArea = useMemo(
    () => (
      <StyledAnnotationsArea breakAt={breakAt}>
        <AnnotationsTitlebar>
          <Toolbar>
            <Toolbar.Flex>
              <span>Margin Notes</span>
            </Toolbar.Flex>
            <div>
              <Button
                title="Close"
                variant="plain"
                onClick={() => {
                  setIsAreaOpen(false);
                  onRequestAside(asideRequest, false);
                }}
              >
                <IconRejectLine width="16" />
              </Button>
            </div>
          </Toolbar>
        </AnnotationsTitlebar>
        <AnnotationsBody>
          <AnnotationsPanel
            annotations={annotations ?? []}
            onCreateAnnotation={onCreateAnnotation}
            onUpdateAnnotation={onUpdateAnnotation}
            onDeleteAnnotation={onDeleteAnnotation}
            isLoading={isLoading}
          />
        </AnnotationsBody>
        <KeyboardAccessible>
          <Button
            stretch
            title="Close"
            onClick={() => {
              setIsAreaOpen(false);
              onRequestAside(asideRequest, false);
            }}
          >
            Close
          </Button>
        </KeyboardAccessible>
      </StyledAnnotationsArea>
    ),
    [
      breakAt,
      annotations,
      onCreateAnnotation,
      onUpdateAnnotation,
      onDeleteAnnotation,
      isLoading,
      onRequestAside,
      asideRequest,
    ],
  );

  return (
    <div style={{ display: "inline-block", position: "relative" }}>
      <StyledAnnotationButton
        title="Annotations"
        onClick={() => {
          setIsAreaOpen(!isAreaOpen);
          onRequestAside(asideRequest, !isAreaOpen);
        }}
        variant="outlined"
        badgeCount={annotationCount}
        hasBadge={annotationCount > 0}
      >
        <IconStickyNoteLine width="16" />
      </StyledAnnotationButton>
      {isAreaOpen
        ? portalTarget && shouldBreakIntoAside
          ? createPortal(AnnotationsArea, portalTarget)
          : AnnotationsArea
        : null}
    </div>
  );
};

const useAnnotations = ({
  locationKey,
  resource,
  setIsLoading,
}: AnnotationsProps & {
  setIsLoading: (isLoading: boolean) => void;
}) => {
  const client = useQueryClient();
  const [state] = useAppContext();

  const resourceId = resource?.id;
  const workspaceId = state.workspaceUser?.id;
  const userId = state.user?.id;

  const annotationsResult = useQueryListAnnotations({
    resourceId,
    workspaceId,
    userId,
    locationKey,
  });

  const updateQueryData = useCallback(
    (fn: (old: Annotation[]) => void) => {
      client.setQueryData(
        ["annotations", resourceId, locationKey, workspaceId, userId],
        (old: Annotation[] | undefined) => {
          if (old == null) return old;
          const next = produce(old, fn);
          return next;
        },
      );
    },
    [client, locationKey, resourceId, userId, workspaceId],
  );

  const createMutation = useMutationCreateAnnotation();
  const updateMutation = useMutationUpdateAnnotation();
  const deleteMutation = useMutationDeleteAnnotation();
  const deleteResource = useMutationDeleteResource();

  const onCreateAnnotation = useCallback(
    async (params: CreateAnnotationParams) => {
      const { skResourceId, bankId } = params;
      if (workspaceId == null) return;
      setIsLoading(true);

      const annotation: Annotation = {
        id: resourceId,
        sk: `annotation#${skResourceId}#${new Date().toISOString()}`,
        schemaVersion: "2020-01-01",
        workspaceId,
        bankId,
        content: {},
        tags: [{ key: "annotation-location-key", value: locationKey }],
        resourceId: params.resource?.id
          ? `${params.resource?.id}/${params.resource?.sk}`
          : undefined,
      };

      try {
        await createMutation.mutateAsync({
          input: {
            ...annotation,
            id: resourceId,
            workspaceId: annotation.workspaceId ?? workspaceId,
          },
        });

        updateQueryData((draft) => {
          annotation.resource = params.resource;
          annotation.creator = state?.user;
          draft.push(annotation);
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      workspaceId,
      setIsLoading,
      resourceId,
      locationKey,
      createMutation,
      updateQueryData,
      state?.user,
    ],
  );

  const onUpdateAnnotation = useCallback(
    async ({ creator, resource, ...annotation }: Annotation) => {
      setIsLoading(true);
      try {
        await updateMutation.mutateAsync({ input: annotation });
        updateQueryData((old) => {
          for (let i = 0; i < old.length; i++) {
            if (old[i].id === annotation.id && old[i].sk === annotation.sk) {
              old[i] = annotation as Annotation;
            }
          }
        });
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, updateMutation, updateQueryData],
  );

  const onDeleteAnnotation = useCallback(
    async (annotation: Annotation) => {
      setIsLoading(true);
      try {
        await deleteMutation.mutateAsync({
          input: { id: annotation.id, sk: annotation.sk },
        });
        if (annotation.resourceId != null) {
          await deleteResource.mutateAsync({
            id: annotation.resourceId.split("/")[0],
            sk: annotation.resourceId.split("/")[1],
          });
        }
        updateQueryData((old) => {
          return old?.filter(
            (a) => a.id !== annotation.id || a.sk !== annotation.sk,
          );
        });
      } finally {
        setIsLoading(false);
      }
    },
    [deleteMutation, deleteResource, setIsLoading, updateQueryData],
  );

  return {
    annotationsResult,
    onCreateAnnotation,
    onUpdateAnnotation,
    onDeleteAnnotation,
  };
};
